import React, { useEffect, useRef, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
} from "reactstrap"
import PropTypes from "prop-types"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import { Table as AntdTable, Popconfirm, Select } from "antd"
import { dateFormatter, dateSorter, defaultPageSizeOptions, numberSorter, showToast, stringSorter } from "utils/general"
import { tableDefaultHeight } from "constants/layout"
import { listQuickGuides, resetQuickGuideRatings } from "helpers/backend_helper"
import QuickGuide from "components/QuickGuides/QuickGuide"
import { homeSubPages } from "pages/Clients/Clients"
import { reportingSubPages } from "pages/Reporting/reporting"
const { Option } = Select

const QuickGuidesManagement = props => {
  const [data, setData] = useState([])
  const [title] = useState(props.t("Quick guides"))
  const [loading, setLoading] = useState(false)
  const [quickGuide, setQuickGuide] = useState(false)
  const [selectedSource, setSelectedSource] = useState("")
  const [selectedTitle, setSelectedTitle] = useState("")
  const [resetConfirm, setResetConfirm] = useState(false)
  const [subPages, setSubPages] = useState()
  const [homeSubs] = useState(homeSubPages)
  const [reportingSubs] = useState(reportingSubPages)
  const [activeSubPage, setActiveSubPage] = useState()

  const abortRef = useRef(null)
  useEffect(() => {
    document.title = title + " | Suvanto Care"
    abortRef.current = new AbortController()
    getGuides()
    return () => abortRef.current.abort()
  }, [])

  // Get guides for table
  const getGuides = () => {
    if (!loading) {
      setLoading(true)
      listQuickGuides(abortRef.current)
        .then(result => {
          if (result.statusCode == 200 && result.entries) {
            for (const element of result.entries) {
              switch (element.pageId) {
                case "test": element.title = "Test"; break
                case "landingPage": element.title = "Front page"; break
                case "clients": element.title = "Customer management"; break
                case "appointments": element.title = "Appointments"; break
                case "userProfile": element.title = "My profile"; break
                case "alarmMonitoring": element.title = "Alarm monitoring"; break
                case "reporting": element.title = "Reporting"; break
                case "groupVideoCall": element.title = "Group video call"; break
                case "videoAppSettings": element.title = "Video app settings"; break
                case "videoContent": element.title = "Content events"; break
                case "videoGroupMessage": element.title = "Group messages"; break
                case "appUsers": element.title = "Mobile app users"; break
                case "userManagement": element.title = "User management"; break
                default: element.title = element.pageId; break
              }
            }
            setData(result.entries)
          } else showToast(props.t("Error"), "error")
        })
        .catch(e => {
          console.log(e)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  // Reset ratings
  const resetRatings = (pageId) => {
    if (!loading) {
      setLoading(true)
      resetQuickGuideRatings(abortRef.current, pageId)
        .then(result => {
          if (result.statusCode == 200) {
            getGuides()
          } else showToast(props.t("Error"), "error")
        })
        .catch(e => {
          console.log(e)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }

  // Table columns
  const columns = [
    {
      dataIndex: "actions",
      title: props.t("Edit"),
      align: "center",
      render: editFormatter,
      fixed: "left",
      width: "100px",
      ellipsis: true,
    },
    {
      dataIndex: "title",
      title: props.t("Title"),
      sorter: (a, b, sortOrder) => {
        if (a["title"] && b["title"]) {
          return props.t(a["title"]).localeCompare(props.t(b["title"]))
        } else if (a["title"]) {
          return sortOrder === "ascend" ? -1 : 1
        } else if (b["title"]) {
          return sortOrder === "ascend" ? 1 : -1
        }
        return 0
      },
      render: (cell) => {
        return <span>{props.t(cell)}</span>
      },
      defaultSortOrder: "ascend",
      width: "300px",
      ellipsis: true,
    },
    {
      dataIndex: "pageId",
      title: props.t("ID"),
      sorter: (a, b, sortOrder) => {
        return stringSorter(a, b, "pageId", sortOrder)
      },
      width: "200px",
      ellipsis: true,
    },
    {
      dataIndex: "updatedAt",
      title: props.t("Updated"),
      sorter: (a, b, sortOrder) => {
        return dateSorter(a["updatedAt"], b["updatedAt"], sortOrder)
      },
      render: dateFormatter,
      width: "160px",
      ellipsis: true,
    },
    {
      dataIndex: "positiveRatings",
      title: props.t("Positive ratings"),
      sorter: (a, b, sortOrder) => {
        return numberSorter(a["positiveRatings"], b["positiveRatings"], sortOrder)
      },
      width: "150px",
      ellipsis: true,
    },
    {
      dataIndex: "negativeRatings",
      title: props.t("Negative ratings"),
      sorter: (a, b, sortOrder) => {
        return numberSorter(a["negativeRatings"], b["negativeRatings"], sortOrder)
      },
      width: "150px",
      ellipsis: true,
    },
    {
      dataIndex: "actions",
      title: props.t("Reset ratings"),
      align: "center",
      render: (cell, row) => {
        return (
          <Button
            className="ms-2"
            color="danger"
            outline
            size="sm"
            disabled={loading}
            onClick={() => {
              resetRatings(row.pageId)
            }}
          >
            <i className="mdi mdi-restart" />
          </Button>
        )
      },
      width: "100px",
      ellipsis: true,
    },
  ]

  function editFormatter(cell, row) {
    return (
      <div className="d-inline-flex">
        <Button
          color="primary"
          outline
          size="sm"
          onClick={() => {
            if (!quickGuide) {
              setSelectedSource(row.pageId)
              setSelectedTitle(row.title)

              if (row.pageId == "clients") setSubPages(homeSubs)
              else if (row.pageId == "reporting") setSubPages(reportingSubs)
              else setSubPages(null)

              setQuickGuide(true)
            }
            else setQuickGuide(false)
          }}
        >
          <i className="mdi mdi-pencil" />
        </Button>
      </div>
    )
  }

  return (
    <>
      <div className="page-content">
        <Container fluid={true} className={quickGuide ? "quick-guide-open" : ""}>
          <Breadcrumbs
            title={props.t("Quick guides")}
            breadcrumbItem={
              <>
                <div className="d-inline-block">
                  <QuickGuide
                    source={selectedSource}
                    title={selectedTitle}
                    isOpen={quickGuide}
                    setIsOpen={setQuickGuide}
                    hidden={true}
                    subPages={subPages}
                    activeSubPageId={activeSubPage}
                  />
                </div>
              </>
            }
          />
          <Card>
            <CardBody>
              <Row>
                <Col>
                  <Button
                    color="primary"
                    className="mb-3 compact-button"
                    disabled={loading}
                    onClick={() => {
                      getGuides()
                    }}
                  >
                    <i className="mdi mdi-database-search me-2" />
                    {props.t("Refresh")}
                  </Button>
                  <Popconfirm
                    title={props.t("Confirm action")}
                    open={resetConfirm}
                    okType="danger"
                    okText={props.t("Reset ratings")}
                    cancelText={props.t("Cancel")}
                    onConfirm={() => {
                      resetRatings(undefined)
                      setResetConfirm(false)
                    }}
                    onCancel={() => {
                      setResetConfirm(false)
                    }}
                  >
                    <Button
                      color="danger"
                      outline
                      className="mb-3 ms-3 compact-button"
                      disabled={loading}
                      onClick={() => {
                        setResetConfirm(true)
                      }}
                    >
                      <i className="mdi mdi-restart me-2" />
                      {props.t("Reset ratings")}
                    </Button>
                  </Popconfirm>
                </Col>
                {subPages != null && quickGuide &&
                  <Col className="col-auto">
                    <span className="me-2 font-size-14 font-weight-semibold">{props.t("Subpage") + ":"}</span>
                    <Select
                      style={{ width: 180 }}
                      allowClear
                      placeholder={props.t("Select")}
                      value={activeSubPage}
                      onChange={value => {
                        setActiveSubPage(value)
                      }}
                    >
                      {subPages?.map(sp => (
                        <Option key={sp.id} value={sp.id}>
                          {props.t(sp.name)}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                }
              </Row>
              <Row className="mt-1">
                <Col>
                  <AntdTable
                    className="colored-header narrow-header"
                    bordered
                    size="small"
                    rowKey={"pageId"}
                    columns={columns}
                    dataSource={data}
                    //rowSelection={rowSelection}
                    pagination={{
                      showSizeChanger: true,
                      defaultPageSize: 50,
                      pageSizeOptions: defaultPageSizeOptions,
                    }}
                    loading={loading}
                    tableLayout="auto"
                    scroll={{
                      x: "max-content",
                      y: tableDefaultHeight,
                    }}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

QuickGuidesManagement.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(QuickGuidesManagement)